import { Phone, Mail, MapPin, Navigation, Send, Youtube, Instagram } from "lucide-react";
import { MessageCircle, ArrowUp } from 'lucide-react';

import React, { useState, useEffect } from 'react';

const Footer = () => {
  const [showScrollTop, setShowScrollTop] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setShowScrollTop(window.pageYOffset > 300);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  return (
    <footer className="py-10 sm:py-12" style={{ backgroundColor: '#134E4A' }}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 mb-8">
          {/* Contact Info */}
          <div>
            <div className="mb-4">
              <img src="/logo.png" alt="DimensionIAS Logo" className="h-11 w-auto object-contain brightness-0 invert" />
            </div>
            <div className="space-y-3 text-sm" style={{ color: '#F0FDF7' }}>
              <div className="flex items-center gap-3">
                <Phone size={18} style={{ color: '#2DD4BF' }} />
                <span>+91 7530807530</span>
              </div>
              <div className="flex items-start gap-2">
                <div className="flex flex-col gap-2">
                  <a 
                    href="https://mail.google.com/mail/?view=cm&fs=1&to=contact@dimensionias.com" 
                    className="flex items-center gap-2 transition-colors duration-200"
                    style={{ color: '#F0FDF7' }}
                    target="_blank" rel="noopener noreferrer"
                    onMouseEnter={e => e.currentTarget.style.color = '#2DD4BF'}
                    onMouseLeave={e => e.currentTarget.style.color = '#F0FDF7'}
                  >
                    <Mail size={14} />
                    <span className="font-semibold">Email:</span>
                    contact@dimensionias.com
                  </a>

                  <a 
                    href="https://mail.google.com/mail/?view=cm&fs=1&to=dimensioniasofficial@gmail.com" 
                    className="flex items-center gap-2 transition-colors duration-200"
                    style={{ color: '#F0FDF7' }}
                    target="_blank" rel="noopener noreferrer"
                    onMouseEnter={e => e.currentTarget.style.color = '#2DD4BF'}
                    onMouseLeave={e => e.currentTarget.style.color = '#F0FDF7'}
                  >
                    <Mail size={14} />
                    <span className="font-semibold">Email:</span>
                    dimensioniasofficial@gmail.com
                  </a>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <MapPin size={18} style={{ color: '#2DD4BF' }} />
                <span>
                  Bedekar Building opposite Vijay Talkies Bus Stop,
                  <br />
                  Near ZUDIO, Laxmi Road, Narayan Peth, Pune 411030
                </span>
              </div>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-xl font-semibold mb-4" style={{ color: '#2DD4BF' }}>Quick Links</h3>
            <ul className="space-y-2 text-sm" style={{ color: '#F0FDF7' }}>
              {[
                { label: "Foundation Program", path: "/courses" },
                { label: "Contact Us", path: "/contact" },
                { label: "Google Map", path: "https://share.google/GhpeLF12z8B5ySA1h" },
              ].map(({ label, path }) => (
                <li key={label}>
                  <a
                    href={path}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="transition-colors duration-200"
                    style={{ color: '#F0FDF7' }}
                    onMouseEnter={e => e.currentTarget.style.color = '#2DD4BF'}
                    onMouseLeave={e => e.currentTarget.style.color = '#F0FDF7'}
                  >
                    {label}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Study Resources */}
          <div>
            <h3 className="text-xl font-semibold mb-4" style={{ color: '#2DD4BF' }}>Study Resources</h3>
            <ul className="space-y-2 text-sm" style={{ color: '#F0FDF7' }}>
              {[
                { label: "Previous Year Papers", path: "/downloads" },
                { label: "Video Lectures", path: "https://www.youtube.com/@DimensionIAS" },
                { label: "Study Notes", path: "/downloads" },
                { label: "Mock Tests", path: "/downloads" },
              ].map(({ label, path }) => (
                <li key={label}>
                  <a
                    href={path}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="transition-colors duration-200"
                    style={{ color: '#F0FDF7' }}
                    onMouseEnter={e => e.currentTarget.style.color = '#2DD4BF'}
                    onMouseLeave={e => e.currentTarget.style.color = '#F0FDF7'}
                  >
                    {label}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Social Media */}
          <div>
            <h3 className="text-xl font-semibold mb-4" style={{ color: '#2DD4BF' }}>Follow Us</h3>
            <div className="flex gap-4">
              {[
                {
                  icon: <MessageCircle size={20} />,
                  name: "WhatsApp",
                  url: "https://wa.me/917530807530",
                },
                {
                  icon: <Send size={20} />,
                  name: "Telegram",
                  url: "https://t.me/DimensionIAS",
                },
                {
                  icon: <Youtube size={20} />,
                  name: "YouTube",
                  url: "https://www.youtube.com/@DimensionIAS",
                },
                {
                  icon: <Instagram size={20} />,
                  name: "Instagram",
                  url: "https://www.instagram.com/dimensionias_pune?igsh=ajRyd3Q1Z2JvenU=",
                },
              ].map((social, index) => (
                <a
                  key={index}
                  href={social.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={social.name}
                  className="w-10 h-10 rounded-full flex items-center justify-center transition-all duration-200 hover:shadow-xl transform hover:scale-110"
                  style={{ backgroundColor: '#2DD4BF', color: 'white' }}
                  onMouseEnter={e => {
                    e.currentTarget.style.backgroundColor = '#F0FDF7';
                    e.currentTarget.style.color = '#134E4A';
                  }}
                  onMouseLeave={e => {
                    e.currentTarget.style.backgroundColor = '#2DD4BF';
                    e.currentTarget.style.color = 'white';
                  }}
                >
                  <span>{social.icon}</span>
                </a>
              ))}
            </div>
          </div>
        </div>

        {/* Map Section */}
        <div className="mb-8 text-center">
          <h3 className="text-xl font-semibold mb-4" style={{ color: '#2DD4BF' }}>Visit Our Location</h3>
          <div
            className="relative max-w-4xl mx-auto rounded-lg overflow-hidden shadow-lg cursor-pointer"
            onClick={() =>
              window.open(
                "https://share.google/GhpeLF12z8B5ySA1h",
                "_blank"
              )
            }
          >
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3783.0848383841665!2d73.85545431489595!3d18.520430787384865!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3bc2c077b8a80113%3A0x86b7c1b6f9c3d9f8!2sBedekar%20Building%2C%20Laxmi%20Rd%2C%20Narayan%20Peth%2C%20Pune%2C%20Maharashtra%20411030!5e0!3m2!1sen!2sin!4v1234567890123!5m2!1sen!2sin"
              width="100%"
              height="280"
              style={{ 
                border: 0, 
                filter: "hue-rotate(180deg) saturate(1.2) brightness(0.9)"
              }}
              allowFullScreen=""
              loading="lazy"
              title="DimensionIAS Location"
              className="pointer-events-none"
            ></iframe>
            <div 
              className="absolute inset-0 flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity duration-300 text-white"
              style={{ backgroundColor: 'rgba(45, 212, 191, 0.2)' }}
            >
              <Navigation size={20} />
              <span className="ml-2 font-semibold">Open in Google Maps</span>
            </div>
          </div>
          <p className="mt-4" style={{ color: '#F0FDF7' }}>
            Bedekar Building opposite Vijay Talkies Bus Stop,
            <br />
            Near ZUDIO, Laxmi Road, Narayan Peth, Pune 411030
          </p>
        </div>

        {/* Footer Bottom */}
        <div className="pt-8 text-center text-sm" style={{ borderTop: '1px solid #2DD4BF', color: '#F0FDF7' }}>
          <p>
            &copy; 2025 DimensionIAS. All rights reserved. |{" "}
            <a
              href="/privacy-policy"
              target="_blank"
              rel="noopener noreferrer"
              className="transition-colors duration-200"
              style={{ color: '#F0FDF7' }}
              onMouseEnter={e => e.currentTarget.style.color = '#2DD4BF'}
              onMouseLeave={e => e.currentTarget.style.color = '#F0FDF7'}
            >
              Privacy Policy
            </a>{" "}
            |{" "}
            <a
              href="/terms-of-service"
              target="_blank"
              rel="noopener noreferrer"
              className="transition-colors duration-200"
              style={{ color: '#F0FDF7' }}
              onMouseEnter={e => e.currentTarget.style.color = '#2DD4BF'}
              onMouseLeave={e => e.currentTarget.style.color = '#F0FDF7'}
            >
              Terms of Service
            </a>
          </p>
        </div>

        {/* Scroll to Top Button */}
        {showScrollTop && (
          <button
            onClick={scrollToTop}
            className="fixed bottom-8 right-8 text-white p-3 rounded-full shadow-lg hover:shadow-xl transform hover:scale-110 transition-all z-50"
            aria-label="Scroll to top"
            style={{ backgroundColor: '#2DD4BF' }}
            onMouseEnter={e => e.currentTarget.style.backgroundColor = '#F0FDF7'}
            onMouseLeave={e => e.currentTarget.style.backgroundColor = '#2DD4BF'}
          >
            <ArrowUp size={20} style={{ color: '#134E4A' }} />
          </button>
        )}
      </div>
    </footer>
  );
};

export default Footer;